import { type EnvironmentId, type ThreadId, type TurnId } from "@cmmd-forge/contracts";
import { type useNavigate } from "@tanstack/react-router";
import { type MutableRefObject, useCallback, useRef } from "react";
import { type DiffRouteSearch } from "../../diffRouteSearch";
import { useRightPanelState, useSeedRightPanelFromLink } from "../../useRightPanel";
import { type DraftId } from "../../draftId";
import { type Thread } from "../../types";
import { useAutoOpenPlanPanel } from "./useAutoOpenPlanPanel";
import { useBrowserAutoRevealPanel } from "./useBrowserAutoRevealPanel";
import { useChatRoutePanelControls } from "./useChatRoutePanelControls";
import { useChatThreadNavigation } from "./useChatThreadNavigation";

export function useChatPanelController(input: {
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly rawSearch: DiffRouteSearch;
  readonly routeKind: "server" | "draft";
  readonly draftId: DraftId | null;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly activeThread: Thread | undefined;
  readonly activeThreadKey: string | null;
  readonly isLocalDraftThread: boolean;
  readonly isServerThread: boolean;
  readonly canOpenRightSidebar: boolean;
  readonly activePlan: { readonly turnId?: TurnId } | null;
  readonly sidebarProposedPlanTurnId: TurnId | null | undefined;
  readonly activeTaskContextExists: boolean;
  readonly latestTurnId: TurnId | null;
  readonly onDiffPanelOpen: (() => void) | undefined;
}) {
  // A shared link seeds the store once, then the store is the only truth.
  useSeedRightPanelFromLink(input.activeThreadKey, input.rawSearch as Record<string, unknown>);
  const rightPanel = useRightPanelState(input.activeThreadKey);
  const panelState = useChatPanelState(input.activeThreadKey);
  const planAutoOpenedForTurnRef = useRef<string | null>(null);
  const resetPlanAutoOpen = useCallback(() => {
    planAutoOpenedForTurnRef.current = null;
  }, []);
  const navigation = useChatThreadNavigation(input);

  useBrowserAutoRevealPanel({
    activeThreadKey: input.activeThreadKey,
    previewOpen: panelState.previewOpen,
  });
  useAutoOpenPlanPanel({
    activePlan: input.activePlan,
    latestTurnId: input.latestTurnId,
    planOpen: panelState.planOpen,
    planAutoOpenedForTurnRef,
    threadKey: input.activeThreadKey,
  });

  return {
    ...useChatPanelControls({
      ...input,
      ...panelState,
      ...navigation,
      planAutoOpenedForTurnRef,
      rightPanelOpen: rightPanel.isOpen,
      openSurfaceCount: rightPanel.surfaces.length,
    }),
    resetPlanAutoOpen,
  };
}

/**
 * Which surface is *showing*, read from the store.
 *
 * These used to be derived from `rawSearch.rightPanel`, which could not
 * distinguish "the diff is open but the panel is collapsed" from "the diff is
 * on screen" — there was no way to express the former. `visibleSurface` is null
 * while the panel is hidden, so a collapsed panel now reports nothing open,
 * which is what every caller of these flags already assumed.
 */
function useChatPanelState(threadKey: string | null) {
  const { visibleSurface } = useRightPanelState(threadKey);
  const kind = visibleSurface?.kind ?? null;
  return {
    diffOpen: kind === "diff",
    previewOpen: kind === "preview",
    canvasOpen: kind === "canvas",
    planOpen: kind === "plan",
  };
}

function useChatPanelControls(input: {
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly navigateActiveThreadSearch: ReturnType<
    typeof useChatThreadNavigation
  >["navigateActiveThreadSearch"];
  readonly navigateToServerThreadSearch: ReturnType<
    typeof useChatThreadNavigation
  >["navigateToServerThreadSearch"];
  readonly rawSearch: DiffRouteSearch;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly activeThread: Thread | undefined;
  readonly isServerThread: boolean;
  readonly canOpenRightSidebar: boolean;
  readonly previewOpen: boolean;
  readonly canvasOpen: boolean;
  readonly diffOpen: boolean;
  readonly planOpen: boolean;
  readonly activePlan: { readonly turnId?: TurnId } | null;
  readonly sidebarProposedPlanTurnId: TurnId | null | undefined;
  readonly activeTaskContextExists: boolean;
  readonly onDiffPanelOpen: (() => void) | undefined;
  readonly planAutoOpenedForTurnRef: MutableRefObject<string | null>;
  readonly rightPanelOpen: boolean;
  readonly openSurfaceCount: number;
  readonly activeThreadKey: string | null;
}) {
  const controls = useChatRoutePanelControls({
    navigate: input.navigate,
    threadKey: input.activeThreadKey,
    environmentId: input.environmentId,
    threadId: input.threadId,
    activeThread: input.activeThread,
    isServerThread: input.isServerThread,
    canOpenRightSidebar: input.canOpenRightSidebar,
    canvasOpen: input.canvasOpen,
    diffOpen: input.diffOpen,
    planOpen: input.planOpen,
    activePlanTurnId: input.activePlan?.turnId,
    sidebarProposedPlanTurnId: input.sidebarProposedPlanTurnId,
    activeTaskContextExists: input.activeTaskContextExists,
    onDiffPanelOpen: input.onDiffPanelOpen,
    markPlanAutoOpenedForTurn: (turnKey) => {
      input.planAutoOpenedForTurnRef.current = turnKey;
    },
  });

  return {
    ...controls,
    previewOpen: input.previewOpen,
    canvasOpen: input.canvasOpen,
    diffOpen: input.diffOpen,
    planOpen: input.planOpen,
    navigateActiveThreadSearch: input.navigateActiveThreadSearch,
    navigateToServerThreadSearch: input.navigateToServerThreadSearch,
  };
}
